import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Container,
  VStack,
  HStack,
  Text,
  Button,
  Heading,
  SimpleGrid,
} from '@chakra-ui/react';
import { initializePaddle } from '@paddle/paddle-js';
import { PADDLE_ENVIRONMENT, PADDLE_TOKEN } from '../paddleConfig';

const FREE_EMAIL_LIMIT = 1000;

const MONTHLY_PRICE = '$3.99';
const YEARLY_PRICE = '$15.99';

// eslint-disable-next-line no-unused-vars
const PADDLE_PRODUCT_ID = 'pro_01kw75f3e2481k5300wc0jzza2';

// Prices are shown statically; only the Paddle price IDs are used for checkout.
const PADDLE_PRICES = [
  { id: 'pri_01kw75wmsjf2fgcg5mjx4m63ra', label: 'Monthly', price: MONTHLY_PRICE, unit: '/ month' },
  { id: 'pri_01kw75j8qny2ebtqvta0hmrs49', label: 'Yearly', price: YEARLY_PRICE, unit: '/ year' },
];

const FREE_FEATURES = [
  'Auto-suggests rules for new senders',
  'Background cleanup',
  'All data stays in your browser',
  'No credit card needed',
];

const UNLIMITED_EXTRA = 'Unlimited cleanup — no monthly caps';

const FAQ_ITEMS = [
  {
    question: `What counts toward the ${FREE_EMAIL_LIMIT}-email limit?`,
    answer: "Only emails Email Cleaner actually removes for you. Emails you read, send, or that don't match any rule are free.",
  },
  {
    question: 'What if I go over on the Free plan?',
    answer: "Cleanup pauses until the 1st of next month. Nothing breaks, nothing's lost — your inbox just goes back to fending for itself for a few days.",
  },
  {
    question: 'Is this safe? Can you see my emails?',
    answer: 'No. Email Cleaner is a browser extension — it talks to Gmail directly using Google\'s official login. Your emails never pass through us. There is no "us" to pass through.',
  },
  {
    question: 'Can I cancel anytime?',
    answer: "Yes. One click in your account settings — no hoops, no guilt trips, no \"are you sure?\" emails.",
  },
  {
    question: 'Will I lose important emails?',
    answer: "You make the rules. You can pause any rule, edit it, or undo it. Nothing gets deleted that you didn't decide to delete.",
  },
];

const CheckIcon = () => (
  <Text as="span" color="#52c41a" fontWeight="bold" lineHeight="1.6" flexShrink={0}>
    &#10003;
  </Text>
);

const PricingStatic = () => {
  const [paddle, setPaddle] = useState(null);
  const [selectedPriceIdx, setSelectedPriceIdx] = useState(0);

  useEffect(() => {
    initializePaddle({
      environment: PADDLE_ENVIRONMENT,
      token: PADDLE_TOKEN,
    }).then((paddleInstance) => {
      if (paddleInstance) {
        setPaddle(paddleInstance);
      }
    });
  }, []);

  const openCheckout = useCallback((priceId) => {
    if (!paddle) return;
    paddle.Checkout.open({
      items: [{ priceId, quantity: 1 }],
      settings: {
        displayMode: 'overlay',
        theme: 'light',
      },
    });
  }, [paddle]);

  const handleSubscribe = () => {
    const priceId = PADDLE_PRICES[selectedPriceIdx]?.id;
    if (!priceId) return;
    openCheckout(priceId);
  };

  const selected = PADDLE_PRICES[selectedPriceIdx];

  return (
    <Box as="main" minH="100vh" bg="gray.50">
      {/* Plans section */}
      <Box>
          <Box pt={{ base: 14, lg: 20 }} pb={{ base: 10, lg: 14 }} />
          <Container maxW="container.md" pb={{ base: 12, lg: 16 }}>
            <SimpleGrid columns={{ base: 1, md: 2 }} spacing={6} alignItems="stretch">

          {/* Free card */}
          <Box
            bg="white"
            border="1px solid"
            borderColor="gray.200"
            borderRadius="lg"
            p={{ base: 6, md: 8 }}
            display="flex"
            flexDirection="column"
          >
            <VStack spacing={6} align="stretch" flex={1}>
              <Box minH="70px">
                <Text fontSize="sm" fontWeight="medium" color="gray.500" textTransform="uppercase" letterSpacing="wide" mb={2}>
                  Free
                </Text>
              </Box>

              <VStack spacing={3} align="stretch">
                <HStack spacing={3} align="flex-start">
                  <CheckIcon />
                  <Text fontSize="sm" color="gray.600" lineHeight="1.6" fontWeight="bold">
                    {FREE_EMAIL_LIMIT} emails / month
                  </Text>
                </HStack>
                {FREE_FEATURES.map((feature) => (
                  <HStack key={feature} spacing={3} align="flex-start">
                    <CheckIcon />
                    <Text fontSize="sm" color="gray.600" lineHeight="1.6">
                      {feature}
                    </Text>
                  </HStack>
                ))}
              </VStack>
            </VStack>
          </Box>

          {/* Unlimited card */}
          <Box
            bg="white"
            border="1px solid"
            borderColor="gray.200"
            borderTop="3px solid #52c41a"
            borderRadius="lg"
            p={{ base: 6, md: 8 }}
            position="relative"
            display="flex"
            flexDirection="column"
          >
            {/* Billing toggle — top right */}
            <HStack spacing={1} position="absolute" top={4} right={4}>
              {PADDLE_PRICES.map((item, idx) => {
                const isSelected = selectedPriceIdx === idx;
                return (
                  <Button
                    key={item.id}
                    onClick={() => setSelectedPriceIdx(idx)}
                    size="xs"
                    variant={isSelected ? 'solid' : 'ghost'}
                    bg={isSelected ? '#52c41a' : 'transparent'}
                    color={isSelected ? 'white' : 'gray.500'}
                    _hover={{
                      bg: isSelected ? '#389e0d' : 'gray.100',
                    }}
                    fontWeight="medium"
                    fontSize="xs"
                    minW="auto"
                    px={2}
                    h="24px"
                  >
                    {item.label}
                  </Button>
                );
              })}
            </HStack>

            <VStack spacing={6} align="stretch" flex={1}>
              <Box minH="70px">
                <Text fontSize="sm" fontWeight="medium" color="gray.500" textTransform="uppercase" letterSpacing="wide" mb={2}>
                  Unlimited
                </Text>

                <Box>
                  <HStack align="baseline" spacing={1}>
                    <Text fontSize="4xl" fontWeight="bold" color="gray.800" lineHeight="1">
                      {selected.price}
                    </Text>
                    <Text fontSize="sm" color="gray.400">{selected.unit}</Text>
                  </HStack>
                  <Text fontSize="xs" color="gray.400" mt={2}>
                    Taxes included
                  </Text>
                </Box>
              </Box>


              <HStack spacing={3} align="flex-start">
                <CheckIcon />
                <Text fontSize="sm" color="#135200" fontWeight="medium" lineHeight="1.6">
                  {UNLIMITED_EXTRA}
                </Text>
              </HStack>

              <Box flex={1} />

              <Button
                onClick={handleSubscribe}
                bg="#52c41a"
                color="white"
                _hover={{ bg: '#389e0d' }}
                size="md"
                isDisabled={!paddle}
              >
                Subscribe
              </Button>
            </VStack>
          </Box>

            </SimpleGrid>
          </Container>
        </Box>

      {/* FAQ */}
      <Box borderTop="1px solid" borderColor="gray.200" bg="white" py={{ base: 12, lg: 16 }}>
        <Container maxW="container.md">
          <Heading
            size="md"
            color="#135200"
            fontWeight="normal"
            mb={8}
          >
            Questions you probably have
          </Heading>
          <VStack spacing={7} align="stretch">
            {FAQ_ITEMS.map((item) => (
              <Box key={item.question}>
                <Text fontWeight="semibold" color="gray.700" mb={1} fontSize="sm">
                  {item.question}
                </Text>
                <Text color="gray.500" lineHeight="relaxed" fontSize="sm">
                  {item.answer}
                </Text>
              </Box>
            ))}
          </VStack>
        </Container>
      </Box>
    </Box>
  );
};

export default PricingStatic;
